"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Page } from "./types";

type Ctx = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  pages: Page[];
  push: (p: Page) => void;
  pop: () => void;
  reset: () => void;
};

const CommandPaletteContext = createContext<Ctx | null>(null);

export function useCommandPalette() {
  const v = useContext(CommandPaletteContext);
  if (!v) throw new Error("useCommandPalette must be used within CommandPaletteProvider");
  return v;
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pages, setPages] = useState<Page[]>([{ kind: "search" }]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((p) => !p), []);
  const push = useCallback((p: Page) => setPages((prev) => [...prev, p]), []);
  const pop = useCallback(() => setPages((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev)), []);
  const reset = useCallback(() => setPages([{ kind: "search" }]), []);

  useEffect(() => {
    if (isOpen) return;
    // wait out the dialog's close animation before clearing the stack so the
    // user never sees the page snap back to root mid-fade
    const t = setTimeout(() => setPages([{ kind: "search" }]), 200);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.key.toLowerCase() !== "k") return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const t = e.target as HTMLElement | null;
      // Yield Cmd+K to TipTap / Monaco / any contenteditable surface that
      // owns its own keybinds. Plain inputs and textareas do not block us
      // — the palette is more important than browser autofill.
      if (t?.isContentEditable) return;
      e.preventDefault();
      setIsOpen((p) => !p);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const value = useMemo<Ctx>(
    () => ({ isOpen, open, close, toggle, pages, push, pop, reset }),
    [isOpen, pages, open, close, toggle, push, pop, reset]
  );

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
}
